import React from "react";
import softdev from "../../assets/images/softdev.png";
import ServicePageShell from "../ServicePageShell/ServicePageShell.jsx";
import { useIsDanish } from "../../i18n/LanguageContext";

const SoftwareDevelopment = () => {
  const isDa = useIsDanish();
  const services = [
    {
      title: "Web Applications",
      desc: "Business web apps, portals, dashboards, and customer-facing systems built for real workflows and secure access.",
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M4 5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M4 8h16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M7 6h.01M10 6h.01M13 6h.01"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      title: "Frontend & Product Interfaces",
      desc: "Fast, clear interfaces for complex systems, built with React and designed around the people who use them every day.",
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M8 21h8M12 17v4M6 3h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
          <path
            d="M9 8 7 10l2 2M15 8l2 2-2 2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      title: "API & Backend Services",
      desc: "Safe APIs, backend services, authentication flows, and business logic that can handle production traffic and audit requirements.",
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M12 3c4.4 0 8 1.6 8 3.5S16.4 10 12 10 4 8.4 4 6.5 7.6 3 12 3Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M4 6.5V12c0 1.9 3.6 3.5 8 3.5s8-1.6 8-3.5V6.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M4 12v5.5C4 19.4 7.6 21 12 21s8-1.6 8-3.5V12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
        </svg>
      ),
    },
    {
      title: "Data & Integration Layer",
      desc: "Databases, data flows, integrations, migrations, and performance tuning so systems exchange information reliably.",
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M12 2c4.4 0 8 1.3 8 3s-3.6 3-8 3-8-1.3-8-3 3.6-3 8-3Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
        </svg>
      ),
    },
    {
      title: "System Integration",
      desc: "Connect existing tools, APIs, SaaS platforms, internal systems, and cloud services without creating fragile point-to-point chaos.",
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M4 4h16v16H4z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M8 9h8M8 13h8M8 17h5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      title: "CI/CD Deployment Pipelines",
      desc: "Every application we build ships through an automated CI/CD pipeline on Azure DevOps or GitHub Actions — never via a publish profile, manual upload, or right-click deploy.",
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path d="M5 3h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M5 13h14a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
          <path d="M9 7h.01M9 17h.01" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      title: "Modernisation & Support",
      desc: "Improve existing applications, replace fragile manual processes, fix reliability issues, and keep systems maintainable.",
      icon: (
        <svg viewBox="0 0 24 24" className="icon" aria-hidden="true">
          <path
            d="M12 2a4 4 0 0 0-4 4v1.1a7 7 0 0 0-3 5.9v2l-1.5 2.5A1 1 0 0 0 4.4 20h15.2a1 1 0 0 0 .9-1.5L19 15v-2a7 7 0 0 0-3-5.9V6a4 4 0 0 0-4-4Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="M9.5 20a2.5 2.5 0 0 0 5 0"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
  ];

  const bullets = [
    "Safe APIs with authentication, validation, and clear ownership",
    "Integrations designed around your existing tools and tenant setup",
    "Backends built for reliability, observability, and future change",
    "Existing systems modernised without unnecessary rebuilds",
    "Always deployed through CI/CD — never a publish profile",
  ];

  const serviceTextsDa = [
    ["Webapplikationer", "Forretningsapps, portaler, dashboards og kundevendte systemer bygget til rigtige arbejdsgange og sikker adgang."],
    ["Frontend og produktinterfaces", "Hurtige og tydelige interfaces til komplekse systemer, bygget med React og designet til de mennesker der bruger dem dagligt."],
    ["API'er og backend services", "Sikre API'er, backend services, authentication flows og forretningslogik, der kan håndtere produktionstrafik og auditkrav."],
    ["Data- og integrationslag", "Databaser, dataflows, integrationer, migreringer og performance tuning, så systemer udveksler information stabilt."],
    ["Systemintegration", "Forbind eksisterende værktøjer, API'er, SaaS-platforme, interne systemer og cloudservices uden skrøbeligt punkt-til-punkt kaos."],
    ["CI/CD deployment pipelines", "Alle applikationer vi bygger leveres gennem automatiserede CI/CD pipelines, ikke publish-profiler, manuelle uploads eller right-click deploys."],
    ["Modernisering og support", "Forbedr eksisterende applikationer, erstat skrøbelige manuelle processer, ret reliability-problemer og hold systemer vedligeholdbare."],
  ];
  const currentServices = isDa
    ? services.map((service, index) => ({ ...service, title: serviceTextsDa[index][0], desc: serviceTextsDa[index][1] }))
    : services;
  const currentBullets = isDa
    ? [
        "Sikre API'er med authentication, validering og tydeligt ejerskab",
        "Integrationer designet omkring jeres eksisterende værktøjer og tenant setup",
        "Backends bygget til reliability, observability og fremtidige ændringer",
        "Eksisterende systemer moderniseret uden unødvendige rebuilds",
        "Altid deployet via CI/CD, aldrig via publish profile",
      ]
    : bullets;

  return (
    <ServicePageShell>
      <div className="sd-wrap">
        {/* HERO */}
        <section className="sd-hero">
          <div className="sd-heroInner">
            <div className="sd-heroText">
              <div className="sd-pill">
                <span className="sd-dot" /> {isDa ? "Softwareudvikling" : "Software Development"}
              </div>

              <h1 className="sd-title">
                {isDa ? "API'er, webapps, integrationer og komplekse systemer bygget til produktion." : <>APIs, web apps, integrations, and complex systems built to survive production.</>}
              </h1>

              <p className="sd-subtitle">
                {isDa ? "Vi bygger nye systemer og forbedrer eksisterende: sikre API'er, stabile backends, integrationer, interne værktøjer og webapps, der passer til den måde jeres organisation faktisk arbejder på." : "We build new systems and improve existing ones: safe APIs, reliable backends, integrations, internal tools, and web apps that fit the way your organisation actually works."}
              </p>

              <div className="sd-ctaRow">
                <a className="sd-btnPrimary" href="/#contact-section">
                  {isDa ? "Kontakt os" : "Contact us"}
                  <span aria-hidden="true" className="sd-arrow">
                    →
                  </span>
                </a>
                <a className="sd-btnGhost" href="/use-cases">
                  {isDa ? "Se cases" : "View use cases"}
                </a>
              </div>

              <div className="sd-trust">
                <div className="sd-trustItem">
                  <strong>{isDa ? "Sikre" : "Safe"}</strong>
                  <span>APIs</span>
                </div>
                <div className="sd-divider" />
                <div className="sd-trustItem">
                  <strong>{isDa ? "Stabile" : "Reliable"}</strong>
                  <span>Backends</span>
                </div>
                <div className="sd-divider" />
                <div className="sd-trustItem">
                  <strong>{isDa ? "Auditerbar" : "Auditable"}</strong>
                  <span>{isDa ? "Levering" : "Delivery"}</span>
                </div>
              </div>
            </div>

            <div className="sd-heroCard">
              <div className="sd-imageFrame">
                <img src={softdev} alt="Software Development" />
              </div>

              <div className="sd-miniGrid">
                <div className="sd-mini">
                  <span className="sd-miniLabel">{isDa ? "Fokus" : "Focus"}</span>
                  <span className="sd-miniValue">{isDa ? "Sikkerhed + skala" : "Safety + Scale"}</span>
                </div>
                <div className="sd-mini">
                  <span className="sd-miniLabel">Stack</span>
                  <span className="sd-miniValue">React • APIs • Integrations</span>
                </div>
                <div className="sd-mini">
                  <span className="sd-miniLabel">{isDa ? "Type" : "Style"}</span>
                  <span className="sd-miniValue">{isDa ? "Eksisterende eller nyt" : "Existing or New"}</span>
                </div>
                <div className="sd-mini">
                  <span className="sd-miniLabel">Support</span>
                  <span className="sd-miniValue">{isDa ? "Løbende" : "Ongoing"}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="sd-glow sd-glow1" />
          <div className="sd-glow sd-glow2" />
        </section>

        {/* SERVICES */}
        <section className="sd-section">
          <div className="sd-sectionHead">
              <h2>{isDa ? "Det vi bygger" : "What We Build"}</h2>
            <p>
              {isDa ? "Produktionsklare applikationer, API'er, integrationer og backend-systemer til organisationer der har brug for mere end en brochure-side." : "Production-ready applications, APIs, integrations, and backend systems for organisations that need more than a brochure site."}
            </p>
          </div>

          <div className="sd-grid">
            {currentServices.map((s) => (
              <div key={s.title} className="sd-card">
                <div className="sd-cardIcon">{s.icon}</div>
                <div className="sd-cardBody">
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* WHY */}
        <section className="sd-section sd-why">
          <div className="sd-whyInner">
            <div className="sd-whyText">
              <h2>{isDa ? "Sådan bygger vi software" : "How we build software"}</h2>
              <p>
                {isDa ? "Vi starter med det system I har, værktøjerne der allerede findes i jeres tenant, og de integrationer forretningen afhænger af. Derefter bygger vi den mindste stabile vej til noget, der er sikrere og lettere at drifte." : "We start with the system you have, the tools already in your tenant, and the integrations your business depends on. Then we build the smallest reliable path to something safer and easier to operate."}
              </p>
            </div>

            <ul className="sd-bullets">
              {currentBullets.map((b) => (
                <li key={b}>
                  <span className="sd-check" aria-hidden="true">
                    ✓
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="sd-cicdRule">
            <div className="sd-cicdIcon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="28" height="28">
                <path d="M5 3h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
                <path d="M5 13h14a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
                <path d="M9 7h.01M9 17h.01" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
              </svg>
            </div>
            <div>
              <h3>{isDa ? "Vores deployment-regel uden undtagelser" : "Our deployment rule — no exceptions"}</h3>
              <p>
                {isDa ? "Vi deployer aldrig via publish profile, FTP eller manuelt right-click. Alle miljøer, dev, staging og produktion, deployes gennem CI/CD pipelines, så hver release er reviewed, testet og auditerbar. Hvis noget fejler, kan det rulles tilbage hurtigt." : "We never deploy using a publish profile, FTP, or a manual right-click. Every environment — dev, staging, production — is deployed through a CI/CD pipeline (Azure DevOps or GitHub Actions) so every release is reviewed, tested, and auditable. If something breaks, you can roll it back in one click."}
              </p>
            </div>
          </div>
          <div className="sd-banner">
            <div>
              <h3>{isDa ? "Klar til at starte?" : "Ready to get started?"}</h3>
              <p>
                {isDa ? "Fortæl hvilket system I skal bygge, forbinde eller fikse, så vender vi tilbage med en klar teknisk vej frem." : "Tell us what system you need to build, connect, or fix — we’ll reply with a clear technical path forward."}
              </p>
            </div>
            <a className="sd-btnPrimary" href="/#contact-section">
              {isDa ? "Lad os tale" : "Let’s talk"} <span aria-hidden="true">→</span>
            </a>
          </div>
        </section>

        {/* Styles */}
        <style>{`
        .sd-wrap{
          max-width: 1100px;
          margin: 0 auto;
          padding: 28px 18px 70px;
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, "Apple Color Emoji", "Segoe UI Emoji";
          color: #0f172a;
        }

        /* HERO */
        .sd-hero{
          position: relative;
          border-radius: 28px;
          padding: 34px 20px;
          overflow: hidden;
          background:
            radial-gradient(1200px 500px at 10% 10%, rgba(59,130,246,0.18), transparent 60%),
            radial-gradient(900px 420px at 90% 10%, rgba(168,85,247,0.16), transparent 58%),
            linear-gradient(180deg, #ffffff, #f8fafc);
          border: 1px solid rgba(15,23,42,0.08);
          box-shadow:
            0 20px 60px rgba(2, 6, 23, 0.10),
            0 2px 12px rgba(2, 6, 23, 0.06);
        }

        .sd-heroInner{
          display: grid;
          grid-template-columns: 1.2fr 0.8fr;
          gap: 22px;
          align-items: center;
          position: relative;
          z-index: 2;
        }

        @media (max-width: 900px){
          .sd-heroInner{
            grid-template-columns: 1fr;
          }
        }

        .sd-pill{
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 8px 12px;
          border-radius: 999px;
          background: rgba(2, 6, 23, 0.04);
          border: 1px solid rgba(15,23,42,0.08);
          font-weight: 600;
          font-size: 0.95rem;
          color: #0f172a;
        }

        .sd-dot{
          width: 10px; height: 10px;
          border-radius: 999px;
          background: linear-gradient(135deg, #3b82f6, #a855f7);
          box-shadow: 0 0 0 3px rgba(59,130,246,0.14);
        }

        .sd-title{
          margin: 14px 0 10px;
          font-size: clamp(2rem, 2.6vw, 3.1rem);
          line-height: 1.06;
          letter-spacing: -0.02em;
        }

        .sd-subtitle{
          margin: 0 0 18px;
          font-size: 1.08rem;
          line-height: 1.6;
          color: rgba(15, 23, 42, 0.78);
          max-width: 58ch;
        }

        .sd-ctaRow{
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 18px;
        }

        .sd-btnPrimary{
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 12px 16px;
          border-radius: 14px;
          text-decoration: none;
          font-weight: 700;
          color: white;
          background: linear-gradient(135deg, #2563eb, #7c3aed);
          box-shadow: 0 14px 30px rgba(37, 99, 235, 0.18);
          border: 1px solid rgba(255,255,255,0.22);
          transition: transform .15s ease, box-shadow .15s ease, filter .15s ease;
        }
        .sd-btnPrimary:hover{
          transform: translateY(-1px);
          filter: brightness(1.02);
          box-shadow: 0 18px 40px rgba(37, 99, 235, 0.22);
        }

        .sd-btnGhost{
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 12px 16px;
          border-radius: 14px;
          text-decoration: none;
          font-weight: 700;
          color: #0f172a;
          background: rgba(255,255,255,0.75);
          border: 1px solid rgba(15,23,42,0.10);
          backdrop-filter: blur(8px);
          transition: transform .15s ease, background .15s ease;
        }
        .sd-btnGhost:hover{
          transform: translateY(-1px);
          background: rgba(255,255,255,0.95);
        }

        .sd-trust{
          display: inline-flex;
          align-items: center;
          gap: 14px;
          padding: 12px 14px;
          border-radius: 18px;
          background: rgba(255,255,255,0.70);
          border: 1px solid rgba(15,23,42,0.08);
          backdrop-filter: blur(10px);
        }
        .sd-trustItem{
          display: grid;
          line-height: 1.1;
        }
        .sd-trustItem strong{
          font-size: 1rem;
        }
        .sd-trustItem span{
          font-size: 0.9rem;
          color: rgba(15,23,42,0.7);
        }
        .sd-divider{
          width: 1px;
          height: 28px;
          background: rgba(15,23,42,0.12);
        }

        /* Right card */
        .sd-heroCard{
          border-radius: 22px;
          background: rgba(255,255,255,0.70);
          border: 1px solid rgba(15,23,42,0.10);
          box-shadow: 0 18px 50px rgba(2, 6, 23, 0.10);
          padding: 16px;
          backdrop-filter: blur(10px);
        }

        .sd-imageFrame{
          border-radius: 18px;
          padding: 14px;
          background:
            radial-gradient(450px 180px at 50% 0%, rgba(59,130,246,0.18), transparent 60%),
            radial-gradient(420px 180px at 50% 100%, rgba(168,85,247,0.16), transparent 60%),
            linear-gradient(180deg, rgba(2,6,23,0.03), rgba(2,6,23,0.01));
          border: 1px solid rgba(15,23,42,0.08);
          display: grid;
          place-items: center;
          min-height: 220px;
        }
        .sd-imageFrame img{
          max-width: 320px;
          width: 100%;
          height: auto;
          border-radius: 14px;
          filter: drop-shadow(0 18px 22px rgba(2,6,23,0.18));
          transform: translateY(2px);
        }

        .sd-miniGrid{
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-top: 12px;
        }
        .sd-mini{
          padding: 12px 12px;
          border-radius: 16px;
          background: rgba(255,255,255,0.72);
          border: 1px solid rgba(15,23,42,0.08);
        }
        .sd-miniLabel{
          display: block;
          font-size: 0.82rem;
          color: rgba(15,23,42,0.62);
          margin-bottom: 4px;
          font-weight: 600;
        }
        .sd-miniValue{
          display: block;
          font-size: 0.98rem;
          font-weight: 800;
          letter-spacing: -0.01em;
        }

        /* Decorative glows */
        .sd-glow{
          position: absolute;
          inset: auto;
          width: 320px;
          height: 320px;
          border-radius: 999px;
          filter: blur(40px);
          opacity: 0.6;
          z-index: 1;
          pointer-events: none;
        }
        .sd-glow1{
          left: -120px;
          top: -120px;
          background: rgba(59,130,246,0.35);
        }
        .sd-glow2{
          right: -120px;
          top: -120px;
          background: rgba(168,85,247,0.30);
        }

        /* SECTIONS */
        .sd-section{
          margin-top: 34px;
        }

        .sd-sectionHead{
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 14px;
          margin-bottom: 14px;
        }
        .sd-sectionHead h2{
          margin: 0;
          font-size: 1.7rem;
          letter-spacing: -0.02em;
        }
        .sd-sectionHead p{
          margin: 0;
          color: rgba(15,23,42,0.72);
          max-width: 62ch;
          line-height: 1.55;
        }

        @media (max-width: 860px){
          .sd-sectionHead{
            flex-direction: column;
            align-items: flex-start;
          }
        }

        .sd-grid{
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 14px;
        }
        @media (max-width: 980px){
          .sd-grid{
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }
        @media (max-width: 620px){
          .sd-grid{
            grid-template-columns: 1fr;
          }
        }

        .sd-card{
          border-radius: 20px;
          background: white;
          border: 1px solid rgba(15,23,42,0.08);
          box-shadow: 0 10px 30px rgba(2,6,23,0.06);
          padding: 16px;
          display: grid;
          grid-template-columns: 44px 1fr;
          gap: 12px;
          transition: transform .15s ease, box-shadow .15s ease;
          min-height: 124px;
        }
        .sd-card:hover{
          transform: translateY(-2px);
          box-shadow: 0 18px 48px rgba(2,6,23,0.09);
        }

        .sd-cardIcon{
          width: 44px;
          height: 44px;
          border-radius: 14px;
          display: grid;
          place-items: center;
          background: linear-gradient(180deg, rgba(59,130,246,0.12), rgba(168,85,247,0.10));
          border: 1px solid rgba(15,23,42,0.08);
          color: #0f172a;
        }

        .sd-cardBody h3{
          margin: 0;
          font-size: 1.05rem;
          letter-spacing: -0.01em;
        }
        .sd-cardBody p{
          margin: 6px 0 0;
          color: rgba(15,23,42,0.72);
          line-height: 1.55;
          font-size: 0.98rem;
        }

        .icon{
          width: 22px;
          height: 22px;
        }

        /* WHY */
        .sd-why{
          border-radius: 26px;
          padding: 22px;
          background:
            radial-gradient(900px 360px at 10% 20%, rgba(59,130,246,0.12), transparent 55%),
            radial-gradient(900px 360px at 90% 20%, rgba(168,85,247,0.10), transparent 55%),
            linear-gradient(180deg, #ffffff, #f8fafc);
          border: 1px solid rgba(15,23,42,0.08);
          box-shadow: 0 18px 50px rgba(2,6,23,0.07);
        }

        .sd-whyInner{
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px;
          align-items: start;
        }
        @media (max-width: 860px){
          .sd-whyInner{
            grid-template-columns: 1fr;
          }
        }

        .sd-whyText h2{
          margin: 0 0 10px;
          font-size: 1.7rem;
          letter-spacing: -0.02em;
        }
        .sd-whyText p{
          margin: 0;
          color: rgba(15,23,42,0.72);
          line-height: 1.6;
          max-width: 62ch;
        }

        .sd-bullets{
          margin: 0;
          padding: 0;
          list-style: none;
          display: grid;
          gap: 10px;
        }
        .sd-bullets li{
          display: grid;
          grid-template-columns: 24px 1fr;
          gap: 10px;
          align-items: start;
          padding: 12px 12px;
          border-radius: 16px;
          background: rgba(255,255,255,0.75);
          border: 1px solid rgba(15,23,42,0.08);
        }
        .sd-check{
          width: 24px; height: 24px;
          border-radius: 999px;
          display: grid;
          place-items: center;
          background: rgba(37,99,235,0.12);
          border: 1px solid rgba(37,99,235,0.18);
          font-weight: 900;
          line-height: 1;
        }

        .sd-cicdRule{
          margin-bottom: 18px;
          border-radius: 20px;
          padding: 20px;
          background: linear-gradient(135deg, rgba(9,120,238,0.06), rgba(124,58,237,0.05));
          border: 1px solid rgba(9,120,238,0.20);
          display: grid;
          grid-template-columns: 44px 1fr;
          gap: 16px;
          align-items: start;
        }
        .sd-cicdIcon{
          width: 44px; height: 44px;
          border-radius: 14px;
          display: grid;
          place-items: center;
          background: linear-gradient(180deg, rgba(37,99,235,0.14), rgba(124,58,237,0.10));
          border: 1px solid rgba(15,23,42,0.08);
          color: #2563eb;
          flex-shrink: 0;
        }
        .sd-cicdRule h3{
          margin: 0 0 8px;
          font-size: 1.05rem;
          letter-spacing: -0.01em;
          color: #0f172a;
        }
        .sd-cicdRule p{
          margin: 0;
          color: rgba(15,23,42,0.72);
          line-height: 1.6;
          font-size: 0.97rem;
        }
        @media (max-width: 500px){
          .sd-cicdRule{ grid-template-columns: 1fr; }
          .sd-cicdIcon{ display: none; }
        }

        .sd-banner{
          margin-top: 18px;
          border-radius: 22px;
          padding: 18px;
          background: linear-gradient(135deg, rgba(37,99,235,0.12), rgba(124,58,237,0.10));
          border: 1px solid rgba(15,23,42,0.10);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          flex-wrap: wrap;
        }
        .sd-banner h3{
          margin: 0 0 6px;
          font-size: 1.15rem;
          letter-spacing: -0.01em;
        }
        .sd-banner p{
          margin: 0;
          color: rgba(15,23,42,0.72);
          line-height: 1.5;
        }
      `}</style>
      </div>
    </ServicePageShell>
  );
};

export default SoftwareDevelopment;
